import type React from 'react'
import { useEffect, useState } from 'react'
import DefaultModal from '../../../../../components/DefaultModal'
import FormButton from '../../../../../components/FormButton'
import type {
  ComplianceOnboardingAnswersDto,
  ProductSelection,
  Q4BBrokerUse,
  Q4IInvestUse,
  Q5Frequency,
  Q6AnnualVolume,
  Q7SourceOfFunds,
  Q8FundsOwnership,
  Q9Occupation,
  Q10PepRca,
} from '../../../../../types/coreApi-types'
import {
  Q0_OPTIONS,
  Q4B_OPTIONS,
  Q4I_OPTIONS,
  Q5_OPTIONS,
  Q6_OPTIONS,
  Q7_OPTIONS,
  Q8_OPTIONS,
  Q9_OPTIONS,
  Q10_OPTIONS,
  NATIONALITY_OPTIONS,
  type QuestionnaireOption,
} from '../../../../../utils/complianceQuestionnaireOptions'

type CheckboxOption<T extends string> = QuestionnaireOption<T>

interface ComplianceQuestionnaireModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (answers: ComplianceOnboardingAnswersDto) => Promise<void>
  isLoading: boolean
  // Migrated Veli clients don't pick a product — Q0 is hidden and they submit
  // with NO product selected. Asking them "which Penning product?" at this
  // stage is confusing. Their risk score still gets the flat Veli-migration
  // contribution, which is derived server-side from the Veli-origin fact (no
  // product needed); the "at least one product" requirement is dropped for them.
  hideProductSelection?: boolean
}

interface FormState {
  q0Products: Set<ProductSelection>
  q4Broker: Set<Q4BBrokerUse>
  q4Invest: Set<Q4IInvestUse>
  q5: Q5Frequency | ''
  q6: Q6AnnualVolume | ''
  q7: Set<Q7SourceOfFunds>
  q8: Q8FundsOwnership | ''
  q9: Q9Occupation | ''
  q10: Q10PepRca | ''
  nationality: string
}

const emptyState = (): FormState => ({
  q0Products: new Set(),
  q4Broker: new Set(),
  q4Invest: new Set(),
  q5: '',
  q6: '',
  q7: new Set(),
  q8: '',
  q9: '',
  q10: '',
  nationality: '',
})

const ComplianceQuestionnaireModal: React.FC<ComplianceQuestionnaireModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  hideProductSelection = false,
}) => {
  const [state, setState] = useState<FormState>(() => emptyState())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setState(emptyState())
      setError(null)
    }
  }, [isOpen])

  const toggle = <T extends string>(set: Set<T>, value: T): Set<T> => {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    return next
  }

  const showQ4B = state.q0Products.has('Broker')
  const showQ4I = state.q0Products.has('Invest')

  const validate = (): string | null => {
    // Veli clients don't select a product (Q0 is hidden), so the product and
    // its Q4 follow-ups are not required for them.
    if (!hideProductSelection) {
      if (state.q0Products.size === 0) return 'Please pick at least one product (Broker or Invest).'
      if (showQ4B && state.q4Broker.size === 0) return 'Please pick at least one Broker use case.'
      if (showQ4I && state.q4Invest.size === 0) return 'Please pick at least one Invest funding/exit method.'
    }
    if (!state.q5) return 'Please answer how often you expect to use Penning services.'
    if (!state.q6) return 'Please answer your expected annual transaction volume.'
    if (state.q7.size === 0) return 'Please pick at least one source of funds.'
    if (!state.q8) return 'Please answer whether these funds are entirely your own.'
    if (!state.q9) return 'Please answer your current occupation.'
    if (!state.q10) return 'Please answer the PEP declaration.'
    if (!state.nationality) return 'Please select your nationality.'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return
    const errorMessage = validate()
    if (errorMessage) {
      setError(errorMessage)
      return
    }
    setError(null)
    await onSubmit({
      q0Products: Array.from(state.q0Products),
      q4Broker: Array.from(state.q4Broker),
      q4Invest: Array.from(state.q4Invest),
      // v7.9: the Wealth product / Q4-X strategy is gone (the Veli-migration
      // risk is derived server-side). Field retained for v7.8 compatibility;
      // always send empty.
      q4Wealth: [],
      q5: state.q5 as Q5Frequency,
      q6: state.q6 as Q6AnnualVolume,
      q7: Array.from(state.q7),
      q8: state.q8 as Q8FundsOwnership,
      q9: state.q9 as Q9Occupation,
      q10: state.q10 as Q10PepRca,
      declaredNationalityIso: state.nationality,
    })
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      inlineLoading
      title="Risk Assessment Questionnaire"
      maxW="760px"
      whiteBG
    >
      <form onSubmit={handleSubmit}>
        <div className="p-8 space-y-8">
          {!hideProductSelection && (
            <Section title="Which Penning products do you plan to use?">
              <CheckboxList
                options={Q0_OPTIONS}
                selected={state.q0Products}
                onToggle={value =>
                  setState(s => ({ ...s, q0Products: toggle(s.q0Products, value) }))
                }
              />
            </Section>
          )}

          {showQ4B && (
            <Section title="How do you plan to use the Broker?">
              <CheckboxList
                options={Q4B_OPTIONS}
                selected={state.q4Broker}
                onToggle={value =>
                  setState(s => ({ ...s, q4Broker: toggle(s.q4Broker, value) }))
                }
              />
            </Section>
          )}

          {showQ4I && (
            <Section title="Which Invest funding and exit flows will you use?">
              <CheckboxList
                options={Q4I_OPTIONS}
                selected={state.q4Invest}
                onToggle={value =>
                  setState(s => ({ ...s, q4Invest: toggle(s.q4Invest, value) }))
                }
              />
            </Section>
          )}

          <Section title="How often do you expect to use Penning services?">
            <SimpleSelect
              value={state.q5}
              onChange={v => setState(s => ({ ...s, q5: v as Q5Frequency }))}
              options={Q5_OPTIONS}
            />
          </Section>

          <Section title="Expected annual transaction volume?">
            <SimpleSelect
              value={state.q6}
              onChange={v => setState(s => ({ ...s, q6: v as Q6AnnualVolume }))}
              options={Q6_OPTIONS}
            />
          </Section>

          <Section title="Source of the funds you'll be trading with?">
            <p className="text-description -mt-3 mb-2">Pick all that apply.</p>
            <CheckboxList
              options={Q7_OPTIONS}
              selected={state.q7}
              onToggle={value =>
                setState(s => ({ ...s, q7: toggle(s.q7, value) }))
              }
            />
          </Section>

          <Section title="Are these funds entirely your own?">
            <p className="text-description -mt-3 mb-2">
              Penning only accepts customers trading their own funds.
            </p>
            <SimpleSelect
              value={state.q8}
              onChange={v => setState(s => ({ ...s, q8: v as Q8FundsOwnership }))}
              options={Q8_OPTIONS}
            />
          </Section>

          <Section title="What is your current occupation?">
            <SimpleSelect
              value={state.q9}
              onChange={v => setState(s => ({ ...s, q9: v as Q9Occupation }))}
              options={Q9_OPTIONS}
            />
          </Section>

          <Section title="Are you a politically exposed person (PEP), or a close associate of one?">
            <p className="text-description -mt-3 mb-2">
              A PEP holds a public function such as head of state, minister, judge,
              ambassador, military officer at general level, or board member of a
              state-owned company. Close associates include immediate family and
              known business partners.
            </p>
            <SimpleSelect
              value={state.q10}
              onChange={v => setState(s => ({ ...s, q10: v as Q10PepRca }))}
              options={Q10_OPTIONS}
            />
          </Section>

          <Section title="What is your nationality?">
            <SimpleSelect
              value={state.nationality}
              onChange={v => setState(s => ({ ...s, nationality: v }))}
              options={NATIONALITY_OPTIONS}
            />
          </Section>

          {error && (
            <div className="text-warning text-sm">{error}</div>
          )}
        </div>

        <div className="p-6 pt-4 flex justify-center border-t border-black/5 dark:border-white/10">
          <div className="max-w-[600px] w-full space-y-3">
            <FormButton
              title="Submit"
              isLoading={isLoading}
              loadingText="Submitting…"
            />
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="button-secondary w-full"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </DefaultModal>
  )
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h2 className="text-lg font-semibold mb-4">{title}</h2>
    {children}
  </div>
)

interface CheckboxListProps<T extends string> {
  options: CheckboxOption<T>[]
  selected: Set<T>
  onToggle: (value: T) => void
}

const CheckboxList = <T extends string>({ options, selected, onToggle }: CheckboxListProps<T>) => (
  <div className="space-y-2">
    {options.map(option => (
      <label
        key={option.value}
        className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-black/5 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
      >
        <input
          type="checkbox"
          className="mt-1"
          checked={selected.has(option.value)}
          onChange={() => onToggle(option.value)}
        />
        <span>{option.label}</span>
      </label>
    ))}
  </div>
)

interface SimpleSelectProps<T extends string> {
  value: T | ''
  onChange: (value: T) => void
  options: { label: string; value: T }[]
}

const SimpleSelect = <T extends string>({ value, onChange, options }: SimpleSelectProps<T>) => (
  <select
    className="input cursor-pointer"
    value={value}
    onChange={e => onChange(e.target.value as T)}
  >
    <option value="">Select an option</option>
    {options.map(o => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
)

export default ComplianceQuestionnaireModal
